
import {
  computed, defineComponent, onMounted, PropType, Ref, ref, SetupContext, watch,
} from 'vue';
import Calendar from 'primevue/calendar';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';
import { IntervalDateEnum, PrimvueMenuModel } from '@/types';
import { useI18n } from 'vue-i18n';
import moment from 'moment';

export default defineComponent({
  name: 'CustomDatePicker',

  components: {
    Calendar,
    SpmOverlayPanel,
    SpmPanelMenu,
  },

  props: {
    modelValue: {
      type: Object,
      default: () => Object,
    },

    calendarPosition: {
      type: String,
      default: 'bottom',
    },

    noLimit: {
      type: Boolean,
      required: false,
      default: false,
    },

    customMinDate: {
      type: Number,
      required: false,
      default: 0,
    },

    customOptionsItems: {
      type: Array as PropType<any[]>,
      required: false,
      default: () => ([]),
    },

    updateValue: {
      type: Boolean,
      required: false,
      default: false,
    },

    defaultValue: {
      type: String,
      required: false,
      default: '',
    },

    // Nombre de mois affichés côte à côte dans le Calendar quand
    // « Période personnalisée » est choisi. Default = 1 pour conserver
    // le comportement historique sur tous les usages existants.
    numberOfMonths: {
      type: Number,
      required: false,
      default: 1,
    },

    // Active la saisie clavier dans l'input Calendar (format dd/mm/yy).
    // Default = false pour rester aligné sur l'UX historique.
    manualInput: {
      type: Boolean,
      required: false,
      default: false,
    },

    // Classe CSS appliquée au panel/pop-up primevue du Calendar.
    // Sert aux écrans qui veulent restyler le calendrier (densité, couleurs)
    // sans toucher au composant central. Default '' = pas de classe ajoutée.
    panelClass: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: ['update:modelValue', 'reset-update-value', 'value-choosed'],

  setup(props: { modelValue: any; customMinDate: number; noLimit: boolean; customOptionsItems: IntervalDateEnum[]; updateValue: boolean; defaultValue: string; numberOfMonths: number; manualInput: boolean; panelClass: string; }, { emit }: SetupContext) {
    const { t } = useI18n();

    const closePanel = ref(false);
    const chosenInterval: Ref<IntervalDateEnum|'empty'|string> = ref('empty');

    const customDate = ref(props.modelValue.customDateRange && props.modelValue.customDateRange.length > 0
      ? props.modelValue.customDateRange.map((date: string) => moment(date).toDate())
      : [moment().subtract(30, 'days').toDate(), moment().toDate()]);

    if (props.modelValue.interval) {
      chosenInterval.value = props.modelValue.interval;
    }
    if (props.defaultValue) {
      chosenInterval.value = props.defaultValue;
    }

    // Limit the date range to a maximum of one year apart
    const maxDate = ref(props.noLimit ? null : moment().toDate());
    const minDate = ref(!props.customMinDate ? null : moment().subtract(props.customMinDate, 'years').toDate());
    const optionsItems = ref<any[]>([]);

    if (props.customOptionsItems.length > 0) {
      optionsItems.value = props.customOptionsItems.map((key) => ({
          label: t(`customDatePicker.${key}`),
          command: () => {
            chosenInterval.value = key;
            closePanel.value = true;
            emit('value-choosed');
          },
          class: computed(() => (chosenInterval.value === key ? 'activeMenuItem' : '')),
        }));
    } else {
      optionsItems.value = Object.values(IntervalDateEnum).map((key) => ({
        label: t(`customDatePicker.${key}`),
        command: () => {
          chosenInterval.value = key;
          closePanel.value = true;
          emit('value-choosed');
        },
        class: computed(() => (chosenInterval.value === key ? 'activeMenuItem' : '')),
      }));
    }

    // add empty value to the optionsItems
    optionsItems.value.unshift({
      label: t('customDatePicker.empty'),
      command: () => {
        chosenInterval.value = 'empty';
        closePanel.value = true;
        emit('value-choosed');
      },
      class: computed(() => (chosenInterval.value === 'empty' ? 'activeMenuItem' : '')),
    });

    // Borne MAX historique (= today si pas de noLimit). Conservée pour
    // pouvoir y revenir quand l'utilisateur sort du mode customDateRange.
    const baseMaxDate = computed(() => (props.noLimit ? null : moment().toDate()));

    // Recalcule maxDate à `start + 1 an` (capé à today) tant qu'une
    // sélection custom est active. Utilisé sur les deux phases (1er
    // click sans 2e click, ET sélection complète) pour empêcher qu'un
    // re-click ultérieur dépasse la fenêtre de 12 mois — ce qui était
    // possible avant car maxDate était réinitialisé à today après le 2e
    // click.
    const applyRangeMaxBound = (anchorStart: Date) => {
      const oneYearLater = moment(anchorStart).add(1, 'years').toDate();
      const todayDate = moment().toDate();
      maxDate.value = oneYearLater > todayDate && !props.noLimit
        ? todayDate
        : oneYearLater;
    };

    watch([chosenInterval, customDate], (newVal) => {
      if (chosenInterval.value === 'customDateRange') {
        if (customDate.value[0] && !customDate.value[1]) {
          applyRangeMaxBound(customDate.value[0]);
        } else if (customDate.value[0] && customDate.value[1]) {
          // Sélection complète : on garde la contrainte (start + 1 an)
          // pour qu'un éventuel re-click ne permette pas de dépasser
          // 12 mois lors d'une nouvelle sélection.
          applyRangeMaxBound(customDate.value[0]);
          emit('update:modelValue', {
            interval: chosenInterval.value,
            customDateRange: customDate.value,
          });
        }
      } else {
        // Hors mode custom : on relâche la contrainte → max revient
        // à la borne par défaut (today ou null si noLimit).
        maxDate.value = baseMaxDate.value;
        emit('update:modelValue', {
          interval: chosenInterval.value,
        });
      }
      if (props.updateValue) {
        emit('reset-update-value');
      }
    }, { deep: true });

    watch(() => props.updateValue, () => {
      if (props.updateValue) {
        customDate.value = props.modelValue.customDateRange && props.modelValue.customDateRange.length > 0
        ? props.modelValue.customDateRange.map((date: string) => moment(date).toDate())
        : [moment().subtract(30, 'days').toDate(), moment().toDate()];
        if (props.modelValue.interval) {
          chosenInterval.value = props.modelValue.interval;
        }
      }
    }, { deep: true });

    onMounted(() => {
      if (chosenInterval.value === 'customDateRange') {
        if (customDate.value[0] && customDate.value[1]) {
          emit('update:modelValue', {
            interval: chosenInterval.value,
            customDateRange: customDate.value,
          });
        }
      } else {
        // on load: update the value for the parent
        emit('update:modelValue', {
          interval: chosenInterval.value,
        });
      }
    });

    // Concatène la classe par défaut `custom-datepicker-panel` (cible le
    // style global compact ci-dessous) avec la `panelClass` optionnelle
    // passée par le caller. Permet aux consommateurs d'ajouter leurs
    // propres modifiers sans perdre la densification de base.
    const resolvedPanelClass = computed(() => {
      const base = 'custom-datepicker-panel';
      return props.panelClass ? `${base} ${props.panelClass}` : base;
    });

    return {
      t,
      IntervalDateEnum,
      chosenInterval,
      optionsItems,
      minDate,
      maxDate,
      customDate,
      closePanel,
      resolvedPanelClass,
    };
  },

});
